import type { HistoricalReport } from '../../../shared/domain/viewableReports/types.no-deps'
import {
    buildViewablePageReport,
    getLoopUnitsInFlowGroups,
} from '../../../shared/domain/viewableReports/viewablePageReport.no-deps'
import type { CommonDeps } from '../base/CommonDeps'

export const fetchTranslatedHistoricalReport = async (
    deps: CommonDeps,
    historicalReport: HistoricalReport,
    translatedReports: Map<number, HistoricalReport>
): Promise<HistoricalReport> => {
    const { report, reportViewData } = historicalReport

    const cachedTranslation = translatedReports.get(report.id)
    if (cachedTranslation) {
        return cachedTranslation
    }

    const maybeGetAction = report.scheduledInstance
        ? deps.httpClient.getActions(report.scheduledInstance, deps.language).then(({ actions }) => actions[0])
        : null

    const [translatedReport, translatedWorkflow, translatedAction] = await Promise.all([
        deps.httpClient.getReport({ id: report.id, language: deps.language }),

        deps.httpClient.getWorkflow(report.workflow, {
            workflowVersion: report.workflowVersion,
            language: deps.language,
            includeDeleted: true,
            richText: true,
        }),

        maybeGetAction,
    ])

    const loopUnits = getLoopUnitsInFlowGroups(reportViewData.flowGroups)
    const viewablePageReport = buildViewablePageReport({
        report: translatedReport,
        answers: translatedReport.answers,
        flow: translatedWorkflow?.workflow?.flow ?? null,
        title: translatedReport.title || translatedWorkflow.workflow.name.text,
        guideLookup: reportViewData.guideLookup,
        unitTypesInAnswers: reportViewData.unitTypesInAnswers,
        unitsInAnswers: reportViewData.unitsInAnswers,
        subjectUnit: reportViewData.subjectUnit,
        loopUnits,
        ancestorReports: translatedReport.ancestorReports,
    })

    const translated: HistoricalReport = {
        ...historicalReport,
        reportViewData: {
            ...historicalReport.reportViewData,
            ...viewablePageReport,
        },

        // Translates the action's description text.
        ...(translatedAction && {
            originInfo: {
                ...historicalReport.originInfo,
                description: translatedAction.description,
            },
        }),
    }

    translatedReports.set(report.id, translated)

    return translated
}
